import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { CategoryService } from 'app/main/pages/category.service';
import { OrganizationService } from 'app/main/pages/contacts/organizations/organization.service';
import { DocumentTypeFormModalComponent } from 'app/main/pages/general-modals/document-type-form-modal/document-type-form-modal.component';
import { OrganizationModalComponent } from 'app/main/pages/general-modals/organization-modal/organization-modal.component';
import { EditLayoutCallableViewEnum } from 'app/main/pages/settings/documenttype/document-type-settings-modal/contract-control-edit-layout/edit-layout-callable.enum';
import { ICategory } from 'app/main/pages/settings/documenttype/document-type-settings-modal/document-type-settings.dto';
import { SharedStoreFacade } from 'app/main/shared/shared/store/shared-store.facade';
import { forkJoin, map, Observable, tap } from 'rxjs';
import * as SharedSelectors from '../../../../../shared/shared/store/shared-store.selector';
import { IGetCategoriesItemDto } from '../../../../categories.dto';
import { flattenCategories as flattenCategoriesUtil } from '../../../../categories.utils';
import { IDocumentTypesDto } from '../../../dto/document-listing.dto';
import { DocumentListingFacade } from '../../../store/document-listing.facade';
import { AddToContractControlFormHelper } from '../add-to-contract-control-modal.component';
import { IAutoFillUploadFile } from '../create-cc-by-ai-form/ai-cc-import.service';
import { IaiProcessedData } from '../iai-processed-data.interface';


@Component({
  selector: 'app-contract-form',
  templateUrl: './contract-form.component.html',
  styleUrl: './contract-form.component.scss'
})
export class ContractFormComponent implements OnInit, IaiProcessedData {
  @Input() contractForm: FormGroup;
  @Input() documentTypes: IDocumentTypesDto[];
  @Input() aiProcessedData: IAutoFillUploadFile;

  public currentFormData$ = this.documentFacade.contractData$;
  public organizations: { id: number, name: string }[] = []; //TODO: set proper type when CC types will be done
  public categories: IGetCategoriesItemDto[] = [];
  public flattenedCategories: Partial<IGetCategoriesItemDto>[] = [];
  public readonly: boolean = false;
  public searchable: boolean = true;
  public modalRef$: NgbModalRef;
  public selectedDocumentType: IDocumentTypesDto;
  //variable used in commented part of the related template
  public editLayoutCallableViewEnum = EditLayoutCallableViewEnum;
  public loadingOrganizations: boolean;
  public loadingCategories: boolean;
  public isDocumentTypesLoading$: Observable<boolean> = this._store.select(SharedSelectors.isDocumentTypesLoading);
  public categoryFields: ICategory[] = [];


  constructor(
    private documentFacade: DocumentListingFacade,
    private _sharedStoreFacade: SharedStoreFacade,
    private _modalService: NgbModal,
    private _organizationService$: OrganizationService,
    private _categoryService$: CategoryService,
    private _addToContractControlFormHelper: AddToContractControlFormHelper,
    private _store: Store
  ) { }

  ngOnInit(): void {
    this.detectChanges();
    forkJoin({
      organizations: this.fetchOrganizations(),
      categories: this.fetchCategories()
    })
      .subscribe({
        next: () => {
          if (this.aiProcessedData) {
            this.initializeWithAIProcessedData(this.aiProcessedData);
          }
        }
      });

    this.getPreSelectedDocumentType();
  }


  /**
  * Fetches a list of organizations from the organization service.
  * The fetched organizations are stored in the `organizations` property.
  * 
  * @private
  * @returns {void}
  */
  private fetchOrganizations(): Observable<{ id: number, name: string }[]> {
    this.loadingOrganizations = true;
    return this._organizationService$.fetchOrganization(1, '', 'all').pipe(
      tap(() => this.loadingOrganizations = false),
      map(response => {
        this.organizations = response as unknown as { id: number, name: string }[]; //TODO: type
        return this.organizations;
      })
    );
  }



  /**
   * Fetches all categories from the category service and processes the response.
   * The fetched categories are assigned to the `categories` property.
   * The categories are also flattened and assigned to the `flattenedCategories` property.
   *
   * @private
   * @returns {void}
   */
  private fetchCategories(): Observable<Partial<IGetCategoriesItemDto>[]> {
    this.loadingCategories = true;
    return this._categoryService$.fetchCategories('all').pipe(
      tap(() => this.loadingCategories = false),
      map(response => {
        this.categories = response as IGetCategoriesItemDto[] ?? [];
        this.flattenedCategories = [];
        this.flattenCategories((response as IGetCategoriesItemDto[]))
        return this.flattenedCategories;
      })
    );
  }



  /**
   * Retrieves the pre-selected document type based on the `document_type_id` value from the contract form.
   * If a document type is found with the matching ID, it sets the `selectedDocumentType` property to the found document type.
   */
  getPreSelectedDocumentType(): void {
    const documentTypeId = this.contractForm.get('document_type_id')?.value;
    if (documentTypeId != null) {
      const selectedDocumentTypeArr = this.documentTypes.filter(item => item.id == documentTypeId);
      if (selectedDocumentTypeArr.length) {
        this.onDocumentTypeChange(selectedDocumentTypeArr[0]);
      }
    }
  }


  /**
   * Detect changes in form
   * @param data 
   */
  detectChanges() {
    this.documentFacade.updateCurrentFormStatus(this.contractForm.invalid);
    this.documentFacade.updateCurrentFormStep(0);
  }







  /**
   * Opens a modal to add a new organization and updates the contract form with the selected organization.
   * 
   * @param organizationName - The name of the organization to pre-fill in the modal (optional).
   * 
   * This method performs the following actions:
   * 1. Opens the `OrganizationModalComponent` modal.
   * 2. Sets the `organization` property of the modal's component instance to the provided `organizationName`.
   * 3. Waits for the modal to close and retrieves the selected organization.
   * 4. If an organization is selected, updates the contract form with the organization's ID.
   * 5. Adds the selected organization to the `organizations` array and updates the array reference.
   */
  addLinkNewOrganizationModalClick(organizationName: string = ''): void {
    this.modalRef$ = this._modalService.open(OrganizationModalComponent);
    this.modalRef$.componentInstance.organization = organizationName;
    this.modalRef$.result.then((organization: { id: number, name: string }) => {
      if (organization) {
        this.contractForm.patchValue({ organization_id: organization.id })
        this.organizations.push({ id: organization.id, name: organization.name });
        this.organizations = this.organizations.slice(0);
      }
    });
  }





  /**
   * Flattens a hierarchical list of categories into a single-level array.
   * Each sub-category is marked with an `is_sub` property set to 1.
   * Additionally, a placeholder category for adding a new category is appended at the end.
   *
   * @param categories - An array of partial category objects to be flattened.
   */
  flattenCategories(categories: Partial<IGetCategoriesItemDto>[]): void {
    this.flattenedCategories = flattenCategoriesUtil(categories as IGetCategoriesItemDto[]);
  }






  /**
   * Handles the change event for the document type selection.
   *
   * @param documentType - The selected document type.
   *
   * Sets the selected document type and populates the category fields based on the document type's sections.
   * Initializes form controls for these fields if they don't exist.
   */
  onDocumentTypeChange(documentType: IDocumentTypesDto): void {
    this.selectedDocumentType = documentType;
    this.categoryFields = [];
    if(!documentType?.sections) return;
    documentType?.sections?.forEach(section => {
      if (section.name === 'cc_category') {
        section.categories.forEach(category => {
          if (category.status && (!category.category.parent_id || category.category.parent_id == 0)) {
            this.categoryFields.push(category);
          }
        })
      }
    });

    // Initialize form controls for these fields if they don't exist
    this.categoryFields.forEach(field => {
      const controlName = `category_${field.category.id}`;
      if (!this.contractForm.get(controlName)) {
        this.contractForm.addControl(controlName, new FormControl([]));
      }
    });

    // Populate individual category controls from aggregate category_id
    this.populateCategoryControlsFromAggregate();
  }



  /**
   * Handles the change event for the selected categories.
   *
   * @param selectedIds - The array of selected category IDs.
   * @param field - The category field object.
   * @param index - The index of the category field.
   *
   * Sets the value of the form control for the category field to the selected IDs.
   */
  selectedCategoriesChange(selectedIds: number[], field: ICategory, index: number) {
    const controlName = `category_${field.category.id}`;
    if (this.contractForm.get(controlName)) {
      this.contractForm.get(controlName).setValue(selectedIds);
    }
    this.updateAggregateCategoryIds();
  }

  /**
   * Retrieves the name of the form control for a specific category field.
   *
   * @param field - The category field object.
   * @returns {string} The name of the form control for the category field.
   */
  getCategoryControlName(field: ICategory): string {
    return `category_${field.category.id}`;
  }



  getCategoryControlValue(field: ICategory): number[] {
    const controlName = this.getCategoryControlName(field);
    return this.contractForm.get(controlName)?.value ?? [];
  }

  /**
   * Distributes category IDs from the aggregate category_id field to individual category controls
   * based on which category tree they belong to
   */
  private populateCategoryControlsFromAggregate(): void {
    const aggregateCategoryIds = this.contractForm.get('category_ids')?.value;
    // Handle both single ID (from AI usually) and array of IDs
    const categoryIdsArray = Array.isArray(aggregateCategoryIds) 
        ? aggregateCategoryIds 
        : (aggregateCategoryIds ? [Number(aggregateCategoryIds)] : []);

    if (categoryIdsArray.length === 0) {
      return;
    }

    // For each category field, find which IDs from the aggregate belong to its tree
    this.categoryFields.forEach(field => {
      const controlName = this.getCategoryControlName(field);
      const allIdsInTree = this.getAllIdsInCategoryTree(field.category);

      // Filter aggregate IDs to only those that exist in this field's tree
      const idsForThisField = categoryIdsArray.filter(id =>
        allIdsInTree.includes(id)
      );

      // Set the value for this specific category control
      if (idsForThisField.length > 0) {
        this.contractForm.get(controlName)?.setValue(idsForThisField);
      }
    });
  }

  /**
   * Recursively gets all category IDs in a category tree
   */
  private getAllIdsInCategoryTree(category: any): number[] {
    let ids = [category.id];

    const children = category.children || category.sub_category;
    if (children && Array.isArray(children)) {
      children.forEach(child => {
        ids.push(...this.getAllIdsInCategoryTree(child));
      });
    }

    return ids;
  }

  private updateAggregateCategoryIds() {
    let allIds: number[] = [];
    this.categoryFields.forEach(field => {
      const controlName = this.getCategoryControlName(field);
      const val = this.contractForm.get(controlName)?.value;
      if (Array.isArray(val)) {
        allIds.push(...val);
      }
    });
    // Remove duplicates
    allIds = [...new Set(allIds)];
    this.contractForm.get('category_ids')?.setValue(allIds);
  }





  /**
   * Opens a modal to add a new document type. If the document type is successfully added,
   * updates the document types list in the shared store and patches the contract form with
   * the new document type ID.
   *
   * @returns {void}
   */
  addNewDocumentType(): void {
    const documentTypeModalRef$ = this._modalService.open(DocumentTypeFormModalComponent);
    documentTypeModalRef$.result.then((data) => {
      if (data.success == 1) {
        this._sharedStoreFacade.updateDocumentTypesList([data.data])
        this.contractForm.patchValue({ document_type_id: data.data.id });
      }
    });
  }





  /**
   * Initializes the form with AI processed data.
   *
   * @param aiProcessedData - The AI processed data to initialize the form with.
   */
  initializeWithAIProcessedData(aiProcessedData: IAutoFillUploadFile): void {
    const defaultForm = this._addToContractControlFormHelper.getContractFormGroup()?.value;
    const categoryIds = this.categoryExists(aiProcessedData?.category?.id) ? [aiProcessedData?.category?.id] : (defaultForm.category_ids || []);
    const organisationId = this.organizationExists(aiProcessedData?.organization?.id) ? aiProcessedData?.organization?.id : defaultForm.organization_id;

    const contract_control_name = aiProcessedData?.contract_name?.trim().length > 0 ? aiProcessedData?.contract_name : defaultForm.contract_control_name;

    this.contractForm.patchValue({
      contract_control_name: this._addToContractControlFormHelper.getAIValueIfNotModifedByUser(this.contractForm.get('contract_control_name'), contract_control_name),
      category_ids: this._addToContractControlFormHelper.getAIValueIfNotModifedByUser(this.contractForm.get('category_ids'), categoryIds),
      organization_id: this._addToContractControlFormHelper.getAIValueIfNotModifedByUser(this.contractForm.get('organisationId'), organisationId),
    });

    // Populate individual category controls from the newly patched category_id
    this.populateCategoryControlsFromAggregate();
  }


  /**
   * Checks if a category exists in the categories array.
   *
   * @param categoryId - The ID of the category to check.
   * @returns {boolean} True if the category exists, false otherwise.
   */
  private categoryExists(categoryId: number): boolean {
    return this.categories.some(item => item.id == categoryId);
  }

  /**
   * Checks if an organization exists in the organizations array.
   *
   * @param organizationId - The ID of the organization to check.
   * @returns {boolean} True if the organization exists, false otherwise.
   */
  private organizationExists(organizationId: number): boolean {
    return this.organizations.some(item => item.id == organizationId);
  }
}
